import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Heading,
  Image,
  Skeleton,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Markup} from '@amblique/base/app/components/shared/markup';
import {
  PIM_CONFIG_THUMBNAIL_HEIGHT_PX,
  PIM_CONFIG_THUMBNAIL_WIDTH_PX,
} from '@amblique/king-living/app/components/configuration-selector/utils';
import ImageZoomModal, {useImageZoomModalState} from '@amblique/king-living/app/components/image-zoom-modal';
import {CylindoZoomIcon} from '@amblique/king-living/app/components/icons';
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';

function ViewDetailsThumbnailSwiperStatic({intl, productFallback, styles, thumbnail, title}) {
  const thumbnailSlides = useMemo(
    () => (thumbnail || []).map((url, index) => {
      const alt = intl.formatMessage({
        defaultMessage: '{title} — configuration image {number}',
        id: 'view_details.configuration_image.alt',
      }, {
        number: index + 1,
        title: title || productFallback,
      });

      return (
        <Box key={url || index} className="view-details-thumbnail-slide-container" {...styles.thumbnailSlide}>
          <Box className="view-details-thumbnail-slide-content" {...styles.thumbnailSlideHit}>
            <Image
              alt={alt}
              htmlHeight={PIM_CONFIG_THUMBNAIL_HEIGHT_PX}
              htmlWidth={PIM_CONFIG_THUMBNAIL_WIDTH_PX}
              objectFit="contain"
              src={url}
              {...styles.thumbnailImage}
            />
          </Box>
        </Box>
      );
    }),
    [intl, productFallback, styles, thumbnail, title]
  );

  return (
    <Box
      className="view-details-column view-details-thumbnail-swiper-container"
      {...styles.thumbnailSwiperWrapper}
    >
      <Swiper className="configuration-thumbnail-swiper" presets="configuration-thumbnail" variant="configuration-thumbnail">
        {thumbnailSlides}
      </Swiper>
    </Box>
  );
}

ViewDetailsThumbnailSwiperStatic.displayName = 'ViewDetailsThumbnailSwiperStatic';

ViewDetailsThumbnailSwiperStatic.propTypes = {
  intl: PropTypes.object.isRequired,
  productFallback: PropTypes.string.isRequired,
  styles: PropTypes.object.isRequired,
  thumbnail: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
};

function ViewDetailsThumbnailSwiperZoomable({
  desktopLightboxRightInsetPx,
  imageZoomFullScreen,
  intl,
  productFallback,
  styles,
  thumbnail,
  title,
}) {
  const {
    close: closeImageZoom,
    handleCloseComplete,
    imageAlt,
    imageSrc,
    isOpen: isImageZoomOpen,
    open: openImageZoom,
  } = useImageZoomModalState();

  const thumbnailSlides = useMemo(
    () => (thumbnail || []).map((url, index) => {
      const alt = intl.formatMessage({
        defaultMessage: '{title} — configuration image {number}',
        id: 'view_details.configuration_image.alt',
      }, {
        number: index + 1,
        title: title || productFallback,
      });

      const zoomAria = intl.formatMessage({
        defaultMessage: 'View larger image',
        id: 'view_details.thumbnail.zoom_aria',
      });

      return (
        <Box key={url || index} className="view-details-thumbnail-slide-container" {...styles.thumbnailSlide}>
          <Box
            aria-label={zoomAria}
            as="button"
            className="view-details-thumbnail-slide-content"
            type="button"
            {...styles.thumbnailSlideHit}
            onClick={() => openImageZoom(url, alt)}
          >
            <Image
              alt=""
              htmlHeight={PIM_CONFIG_THUMBNAIL_HEIGHT_PX}
              htmlWidth={PIM_CONFIG_THUMBNAIL_WIDTH_PX}
              objectFit="contain"
              src={url}
              {...styles.thumbnailImage}
            />
            <Box aria-hidden {...styles.thumbnailZoomIconBadge}>
              <CylindoZoomIcon {...styles.thumbnailZoomIcon} />
            </Box>
          </Box>
        </Box>
      );
    }),
    [intl, openImageZoom, productFallback, styles, thumbnail, title]
  );

  return (
    <>
      <Box
        className="view-details-column view-details-thumbnail-swiper-container"
        {...styles.thumbnailSwiperWrapper}
      >
        <Swiper className="configuration-thumbnail-swiper" presets="configuration-thumbnail" variant="configuration-thumbnail">
          {thumbnailSlides}
        </Swiper>
      </Box>
      <ImageZoomModal
        desktopLightboxRightInsetPx={desktopLightboxRightInsetPx}
        imageAlt={imageAlt}
        imageSrc={imageSrc}
        imageZoomFullScreen={imageZoomFullScreen}
        isOpen={isImageZoomOpen}
        onClose={closeImageZoom}
        onCloseComplete={handleCloseComplete}
      />
    </>
  );
}

ViewDetailsThumbnailSwiperZoomable.displayName = 'ViewDetailsThumbnailSwiperZoomable';

ViewDetailsThumbnailSwiperZoomable.propTypes = {
  desktopLightboxRightInsetPx: PropTypes.number.isRequired,
  imageZoomFullScreen: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  productFallback: PropTypes.string.isRequired,
  styles: PropTypes.object.isRequired,
  thumbnail: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
};

/**
 * View details block: optional thumbnail swiper, title, short description, long description.
 * Styled via theme key `ViewDetails`.
 *
 * @param {string} [props.title] - Primary heading (e.g. displayTitle / name)
 * @param {string} [props.description] - Short description HTML
 * @param {string} [props.longDescription] - Long description HTML
 * @param {boolean} [props.isLoading] - When true, shows skeleton placeholders
 * @param {boolean} [props.isThumbnailZoomEnabled=true] - When false, thumbnails are static (no lightbox / zoom icon)
 * @param {boolean} [props.imageZoomFullScreen=false] - Simple PDP only: full viewport lightbox
 * @param {boolean} [props.isConfigPanelGalleryDetail=false] - Mobile config panel gallery detail: short desc + link tokens
 * @param {string[]} [props.thumbnail] - Configuration image URLs (c_pimlyConfigurationImage)
 */
function ViewDetails({
  desktopLightboxRightInsetPx = 0,
  title = '',
  description,
  longDescription,
  isLoading = false,
  isThumbnailZoomEnabled = true,
  imageZoomFullScreen = false,
  showMobileConfigThumbnails = false,
  thumbnail = [],
  ...rest
}) {
  const intl = useIntl();
  const styles = useMultiStyleConfig('ViewDetails', {});
  const isMobile = useBreakpointValue({base: true, lg: false});

  const productFallback = intl.formatMessage({
    defaultMessage: 'Product',
    id: 'view_details.product.fallback',
  });

  if (isLoading) {
    return (
      <Box className="view-details-container" {...styles.root} {...rest}>
        <Skeleton height="28px" width="80%" />
        <Skeleton height="48px" marginTop={2} width="100%" />
        <Skeleton height="120px" marginTop={4} width="100%" />
      </Box>
    );
  }

  return (
    <Box className="view-details-container" {...styles.root} {...rest}>
      {thumbnail?.length > 0 && (!isMobile || showMobileConfigThumbnails) && (
        isThumbnailZoomEnabled ? (
          <ViewDetailsThumbnailSwiperZoomable
            desktopLightboxRightInsetPx={desktopLightboxRightInsetPx}
            imageZoomFullScreen={imageZoomFullScreen}
            intl={intl}
            productFallback={productFallback}
            styles={styles}
            thumbnail={thumbnail}
            title={title}
          />
        ) : (
          <ViewDetailsThumbnailSwiperStatic
            intl={intl}
            productFallback={productFallback}
            styles={styles}
            thumbnail={thumbnail}
            title={title}
          />
        )
      )}

      <Box className={`view-details-content-pair ${!thumbnail?.length ? 'view-details-content-pair--no-thumbnail' : ''}`} {...styles.contentPair}>
        <Box className="view-details-column" {...styles.configNameColumn}>
          <Heading {...styles.title}>{title}</Heading>
          {description ? <Markup source={description} {...styles.description} /> : null}
        </Box>

        {longDescription ? (
          <Skeleton isLoaded={Boolean(longDescription)} {...styles.longDescriptionSkeleton}>
            <Box className="view-details-column view-details-product-description-container" {...styles.longDescription}>
              <Markup source={longDescription} />
            </Box>
          </Skeleton>
        ) : null}
      </Box>
    </Box>
  );
}

ViewDetails.displayName = 'ViewDetails';

ViewDetails.propTypes = {
  desktopLightboxRightInsetPx: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
  longDescription: PropTypes.string,
  showMobileConfigThumbnails: PropTypes.bool,
  isLoading: PropTypes.bool,
  isThumbnailZoomEnabled: PropTypes.bool,
  imageZoomFullScreen: PropTypes.bool,
  thumbnail: PropTypes.arrayOf(PropTypes.string),
};

export default ViewDetails;
